import * as csv from '@vanillaes/csv';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { allTranslationLanguages } from '@gitbook/app-translations';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FilesDropZone,
    Heading,
    Text,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';
import { createHTTPError } from '@gitbook/util-debug';
import { filterOutNullable } from '@gitbook/util-ts';

import type { GlossaryStateActions } from './state';

/**
 * Dialog to import a glossary from a CSV file.
 */
export function GlossaryImportDialog(props: { actions: GlossaryStateActions }) {
    const { actions } = props;
    const close = useDialogClose();

    const handleFiles = React.useCallback(
        async (files: File[]) => {
            await toast.catch(async () => {
                const file = files[0];
                if (!file) return;
                const text = await file.text();
                const { entries, languages } = await parseGlossaryCSV(text);
                if (entries.length > 0) {
                    actions.insertGlossaryEntries(entries);
                    actions.setDisplayedLanguages(languages, true);
                    toast.success(`Imported ${entries.length} entries`);
                    close();
                } else {
                    throw new createHTTPError.NotAcceptable(
                        'No entries found in this CSV file, Please check the file format and try again.'
                    );
                }
            }, 'Error while importing glossary');
        },
        [close, actions.insertGlossaryEntries, actions.setDisplayedLanguages]
    );

    return (
        <>
            <DialogHeader>
                <Heading>Upload a glossary</Heading>
                <Description>
                    Provide a CSV file where the first row lists the language codes (for example
                    "en,fr,es"). Each following row represents a glossary entry with a translation
                    for each language. Empty cells will be ignored.
                </Description>
            </DialogHeader>
            <DialogBody>
                <FilesDropZone
                    acceptedFileTypes="csv"
                    onSelectFiles={handleFiles}
                    icon={<Icon.Table />}
                >
                    <Text slot="label">Drop a CSV file here or click to select</Text>
                </FilesDropZone>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
            </DialogFooter>
        </>
    );
}

function parseGlossaryCSV(data: string): {
    entries: api.GlossaryTranslations[];
    languages: api.TranslationLanguage[];
} {
    const parsed = csv.parse(data) as string[][];

    const header = parsed[0];
    if (!header) {
        return {
            entries: [],
            languages: [],
        };
    }

    const languages = header
        .map((language, index) => {
            if (allTranslationLanguages.includes(language as api.TranslationLanguage)) {
                return {
                    index,
                    language: language as api.TranslationLanguage,
                };
            }

            return null;
        })
        .filter(filterOutNullable);
    if (languages.length === 0) {
        return {
            entries: [],
            languages: [],
        };
    }

    const entries: api.GlossaryTranslations[] = [];
    parsed.slice(1).forEach((row) => {
        const translations: api.GlossaryTranslations = {};
        for (const { index, language } of languages) {
            const value = row[index];
            if (value) {
                translations[language] = value;
            }
        }
        if (Object.keys(translations).length > 0) {
            entries.push(translations);
        }
    });

    return {
        entries,
        languages: languages.map(({ language }) => language),
    };
}
